import { CurrencyEnum } from '@app/generated/graphql';

import { Injectable } from '@angular/core';

export interface ICurrency {
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
  countryCode: string;
  label?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  // list of currencies used in the system.
  // ideally this should exist on the backend and be fetched from there, however,
  // for the sake of simplicity it is hardcoded here.
  private _currencies: ICurrency[] = [
    {
      currencyCode: 'USD',
      currencyName: 'United States Dollar',
      currencySymbol: '$',
      countryCode: 'US',
    },
    {
      currencyCode: 'EUR',
      currencyName: 'Euro',
      currencySymbol: '€',
      countryCode: 'EU',
    },
    {
      currencyCode: 'GBP',
      currencyName: 'British Pound Sterling',
      currencySymbol: '£',
      countryCode: 'GB',
    },
    {
      currencyCode: 'CAD',
      currencyName: 'Canadian Dollar',
      currencySymbol: 'C$',
      countryCode: 'CA',
    },
    {
      currencyCode: 'ZAR',
      currencyName: 'South African Rand',
      currencySymbol: 'R',
      countryCode: 'ZA',
    },
    {
      currencyCode: 'JPY',
      currencyName: 'Japanese Yen',
      currencySymbol: '¥',
      countryCode: 'JP',
    },
    {
      currencyCode: 'CNY',
      currencyName: 'Chinese Yuan',
      currencySymbol: '¥',
      countryCode: 'CN',
    },
    {
      currencyCode: 'NAD',
      currencyName: 'Namibian Dollar',
      currencySymbol: 'N$',
      countryCode: 'NA',
    },
  ];

  constructor() {}

  getFlagEmoji(countryCode: string): string {
    if (countryCode?.length !== 2) {
      return;
    }

    // eg. 'US' -> '🇺🇸'
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }

  getFlagEmojiFromCurrency(currencyCode: string): string {
    if (currencyCode?.length !== 3) {
      return;
    }

    // eg. 'usd' -> '🇺🇸'
    const currency = this._currencies?.find(
      (c: ICurrency) => c.currencyCode === currencyCode.toUpperCase(),
    );
    return currency ? this.getFlagEmoji(currency.countryCode) : '';
  }

  getCurrencySymbol(currencyCode: string): string {
    if (currencyCode?.length !== 3) {
      return;
    }

    // eg. 'usd' -> '$'
    const currency = this.allowedCurrencies?.find(
      (c: ICurrency) => c.currencyCode === currencyCode.toUpperCase(),
    );
    return currency ? currency.currencySymbol : '';
  }

  getCountryCurrency(countryCode: string): ICurrency | null {
    if (countryCode?.length !== 2) {
      return;
    }

    // eg. 'US' -> 'USD'
    const currency = this.allowedCurrencies?.find(
      (c: ICurrency) => c.countryCode === countryCode.toUpperCase(),
    );
    return currency ? currency : null;
  }

  getAllCurrencies(): ICurrency[] {
    return this.allowedCurrencies;
  }

  getCurrencyByCode(currencyCode: string): ICurrency | null {
    if (currencyCode?.length !== 3) {
      return;
    }

    return this.allowedCurrencies.find(
      (c: ICurrency) => c.currencyCode === currencyCode.toUpperCase(),
    );
  }

  get allowedCurrencies(): ICurrency[] {
    // only allow currencies that are in the CurrencyEnum this is to
    // prevent users from using currencies that are not supported by the system
    return this._currencies
      .filter((c: ICurrency) =>
        Object.values(CurrencyEnum).includes(c.currencyCode as CurrencyEnum),
      )
      .map((c: ICurrency) => ({
        ...c,
        label: `${this.getFlagEmoji(c.countryCode)} ${c.currencyCode}`,
      }));
  }
}
